import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from '@/App';
import { getMyGrades, getUnofficialTranscriptUrl, triggerDownload } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Award, BookOpen, FileDown, GraduationCap, Lock, TrendingUp } from 'lucide-react';

const formatNumber = (value) => (value === null || value === undefined ? 'N/A' : value);

const gradeTone = (grade) => {
  if (!grade) return 'border-[#062B1A]/10 text-[#062B1A]/55';
  if (grade === 'F' || grade === 'W') return 'border-red-200 bg-red-50 text-red-700';
  if (grade === 'CIP') return 'border-blue-200 bg-blue-50 text-blue-700';
  return 'border-green-200 bg-green-50 text-green-700';
};

export default function AcademicRecord() {
  const { currentStudent, hydrating } = useAppContext();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadRecord = async () => {
      try {
        setLoading(true);
        setError('');
        const { data } = await getMyGrades();
        if (!cancelled) {
          setRecord(data);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError('Academic record could not be loaded.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadRecord();

    return () => {
      cancelled = true;
    };
  }, []);

  const grades = useMemo(() => record?.grades || [], [record?.grades]);
  const studentId = currentStudent?.id || record?.student_id;
  const enrolmentStatus = useMemo(() => {
    if (!grades.length) return 'Active';
    return record?.gpa && record.gpa >= 2 ? 'Graduated' : 'Active';
  }, [grades.length, record?.gpa]);

  const sortedGrades = useMemo(() => {
    const termRank = { Fall: 0, Winter: 1, Spring: 2, Summer: 3 };
    return [...grades].sort((a, b) => {
      const [aSeason = '', aYear = '0'] = (a.term || '').split(' ');
      const [bSeason = '', bYear = '0'] = (b.term || '').split(' ');
      if (aYear !== bYear) return aYear.localeCompare(bYear);
      return (termRank[aSeason] ?? 9) - (termRank[bSeason] ?? 9);
    });
  }, [grades]);

  const handleDownload = async () => {
    if (!studentId) return;
    try {
      setDownloading(true);
      await triggerDownload(getUnofficialTranscriptUrl(studentId));
    } catch (err) {
      console.error(err);
      setError('Unofficial transcript could not be generated.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading || hydrating) {
    return (
      <div className="space-y-6">
        <div className="h-24 bg-white rounded-xl animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((item) => <div key={item} className="h-28 bg-white rounded-xl animate-pulse" />)}
        </div>
        <div className="h-80 bg-white rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!currentStudent && !record?.student_id) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="text-center py-16">
          <GraduationCap className="w-12 h-12 text-[#062B1A]/15 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            Profile not provisioned yet
          </h2>
          <p className="text-sm text-[#062B1A]/50 mt-2 max-w-md mx-auto">
            Your student record has not been linked yet. Contact your administrator if this persists.
          </p>
        </div>
      </div>
    );
  }

  const kpis = [
    { label: 'Weighted GPA', value: record?.gpa === null || record?.gpa === undefined ? 'N/A' : record.gpa.toFixed(2), icon: TrendingUp, accent: true },
    { label: 'Credits Attempted', value: formatNumber(record?.credits_attempted), icon: BookOpen, accent: false },
    { label: 'Credits Earned', value: formatNumber(record?.credits_earned), icon: Award, accent: true },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            Academic Record
          </h2>
          <p className="text-sm text-[#062B1A]/60 mt-1">Grades, credits, GPA, and transcript access</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleDownload}
            disabled={!studentId || downloading}
            className="bg-[#062B1A] text-white hover:bg-[#041B10]"
          >
            <FileDown className="w-4 h-4 mr-2" />
            {downloading ? 'Generating PDF...' : 'Download unofficial transcript'}
          </Button>
          <div className="space-y-1">
            <Button asChild variant="outline" className="w-full border-[#062B1A]/15 text-[#062B1A]">
              <Link to="/student/services">
                <Lock className="w-4 h-4 mr-2" />
                Request official transcript
              </Link>
            </Button>
            <p className="text-xs text-[#062B1A]/45">Open Document Services to review current options and exact tax-exempt pricing.</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">Program</p>
              <p className="mt-1 text-sm font-medium text-[#062B1A]">
                {currentStudent?.program_name || 'Program not assigned'}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">Enrolment Status</p>
              <Badge className="mt-1 border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#062B1A] hover:bg-[#D4AF37]/10">
                {enrolmentStatus}
              </Badge>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">Student ID</p>
              <p className="mt-1 text-sm font-medium text-[#062B1A]">
                {currentStudent?.student_number || record?.student_number || 'N/A'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {kpis.map((kpi) => (
          <Card key={kpi.label} className={`card-hover border border-[rgba(6,43,26,0.08)] shadow-sm ${kpi.accent ? 'gold-border-left' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">{kpi.label}</span>
                <kpi.icon className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>{kpi.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            Course Grades
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sortedGrades.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-10 h-10 text-[#062B1A]/20 mx-auto mb-2" />
              <p className="text-sm text-[#062B1A]/50">No grades recorded yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Course Title</TableHead>
                  <TableHead>Term</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Letter Grade</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedGrades.map((grade) => (
                  <TableRow key={grade.id}>
                    <TableCell className="font-medium text-[#062B1A]">{grade.course_code || 'N/A'}</TableCell>
                    <TableCell className="text-[#062B1A]/75">{grade.course_title || 'Untitled course'}</TableCell>
                    <TableCell className="text-[#062B1A]/65">{grade.term || 'N/A'}</TableCell>
                    <TableCell className="text-[#062B1A]/65">{grade.credits ?? 3}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={gradeTone(grade.letter_grade)}>
                        {grade.letter_grade || 'N/A'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
